import type { PlmObservationInput, PlmOption } from './plm.api';

/**
 * The Stereoscopic Observation option lists — ONE definition shared by the
 * Stereoscopic tab (which renders them as checkboxes) and the Final
 * Confirmation tab (which reads them back as "A, B, C").
 *
 * They used to be defined twice. When Fibrous / Non Fibrous were added to the
 * Stereoscopic tab (2026-09-24) the Final tab's private copy was never updated,
 * so a layer whose only texture was Fibrous read back as "—" (owner report
 * 2026-09-25: "why it is showing ---- it has values in 1st tab"). Its copy had
 * also drifted on labels ("Non-homogeneous" vs "Heterogeneous") and order.
 * Sharing the arrays makes that class of drift impossible.
 */

/** Keys of the observation's boolean (tick-box) columns. */
export type BoolKey = {
  [K in keyof PlmObservationInput]: PlmObservationInput[K] extends boolean ? K : never
}[keyof PlmObservationInput];

/** Keys of the observation's free-text columns. */
export type TextKey = {
  [K in keyof PlmObservationInput]: PlmObservationInput[K] extends string | null ? K : never
}[keyof PlmObservationInput];

export type StereoOption = readonly [BoolKey, string];

/** A tick box whose free-text partner holds the analyst's own wording. */
export type TextPartner = { gate: BoolKey; text: TextKey };

/** The 20 colour boxes, in the legacy detail view's order. */
// Prototype v8's order, read off its two columns ROW-WISE (the card is a
// 2-column grid, which fills across then down):
//   Various Colors | Red      Gray | Blue      Cyan | Yellow   …
// NOT alphabetical — the prototype leads with the catch-all and then runs the
// primaries, which is the order an analyst scans.
export const COLOURS: ReadonlyArray<StereoOption> = [
  ['variousColors', 'Various Colors'], ['colorRed', 'Red'],
  ['colorGray', 'Gray'],       ['colorBlue', 'Blue'],
  ['colorCyan', 'Cyan'],       ['colorYellow', 'Yellow'],
  ['colorMagenta', 'Magenta'], ['colorGreen', 'Green'],
  ['colorTeal', 'Teal'],       ['colorOrange', 'Orange'],
  ['colorBeige', 'Beige'],     ['colorPurple', 'Purple'],
  ['colorTan', 'Tan'],         ['colorPink', 'Pink'],
  ['colorGold', 'Gold'],       ['colorBrown', 'Brown'],
  ['colorSilver', 'Silver'],   ['colorBlack', 'Black'],
  ['colorClear', 'Clear'],     ['colorWhite', 'White'],
  // The prototype's 21st colour. dbo.PLMStereoscopicObservation has the
  // Color_Other column (0 rows in use), it was simply never rendered.
  ['colorOther', 'Other'],
];

// TEXTURE IS NO LONGER ENTERED (owner feedback 2026-10-07: the Texture card is
// gone from the Stereoscopic tab). The list stays because a layer saved before
// that still carries its ticks: they round-trip untouched and the Final
// Confirmation tab reads them back — on such a layer only.
//
// Legacy's Texture column, in ITS order (age1073 PLM screenshot 2026-09-24).
// Fibrous / Non Fibrous were MISSING from this list until then: both columns
// exist on dbo.PLMStereoscopicObservation and in PlmObservationInput, and the
// save/read round-trip carried them, but neither was ever rendered — so the
// analyst had no way to record either observation. They lead the column in
// legacy, so they lead it here.
export const TEXTURES: ReadonlyArray<StereoOption> = [
  ['fibrous', 'Fibrous'], ['nonFibrous', 'Non Fibrous'],
  ['cementitious', 'Cementitious'], ['granular', 'Granular'], ['firm', 'Firm'],
  ['soft', 'Soft'], ['paperLike', 'Paper-Like'], ['otherTexture', 'Other'],
];

/** Sample treatment — the prototype's five, in its order (owner 2026-09-25,
 *  "then match with prototype"). The older AcidTreated / Ashed / Ground / HCL /
 *  Teased columns still exist on the table but the legacy BO stopped mapping
 *  them; they are read-only history (owner decision 2026-09-22). */
export const TREATMENTS: ReadonlyArray<StereoOption> = [
  ['treatmentTease', 'Tease'],   ['treatmentDissolve', 'Dissolve'],
  ['treatmentHeat', 'Heat'],     ['treatmentAsh', 'Ash'],
  ['treatmentGrind', 'Grind'],
];

/**
 * Four MORE treatment columns exist on dbo.PLMStereoscopicObservation that the
 * prototype does not draw. They are not rendered — so a normal record shows
 * exactly the prototype's five — EXCEPT on a record that already has one set,
 * where it is shown so the reading is not silently invisible.
 *
 * Verified on live data (AGE_ALPACALIMS_Test, 19 observations):
 * Matrix 0 · Non-Friable 0 · Solvent-Treated 1 · Other 4. Those five records
 * would otherwise display a Treatment card with nothing ticked while the row
 * says otherwise. The values round-trip either way — the reader returns them
 * and the save writes them back — so nothing is LOST by hiding them; this is
 * about not showing the analyst something untrue.
 */
export const LEGACY_TREATMENTS: ReadonlyArray<StereoOption> = [
  ['treatmentMatrix', 'Matrix'],                  ['treatmentNonFriable', 'Non-Friable'],
  ['treatmentSolventTreated', 'Solvent-Treated'], ['treatmentOther', 'Other'],
];

export const HOMOGENEITY: ReadonlyArray<StereoOption> = [
  ['homogeneous', 'Homogeneous'], ['nonHomogeneous', 'Heterogeneous'],
  ['layered', 'Layered'], ['otherHomogenity', 'Other'],
];

/** Legacy `Friability` enum — 0 is the XAF "Dummy" placeholder, never shown. */
export const FRIABILITY: ReadonlyArray<readonly [number, string]> = [
  [1, 'Yes'], [2, 'No'], [3, 'N/A'],
];

/** Which tick box unlocks which free-text box, per group. Treatment has no
 *  free-text partner on dbo.PLMStereoscopicObservation — its "Other" is a plain
 *  flag and reads as its own value. */
export const TEXTURE_TEXT: TextPartner = { gate: 'otherTexture', text: 'textureText' };
export const HOMOGENEITY_TEXT: TextPartner = { gate: 'otherHomogenity', text: 'homogenityText' };
export const COLOUR_TEXT: TextPartner = { gate: 'variousColors', text: 'colorText' };

const OID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The Material Description of a STORED value (owner feedback 2026-10-07: the
 * material is one free-text box, not a pick list): the text exactly as typed.
 * A layer saved before that stores a ';'-joined list of dbo.Materials Oids
 * instead — that reads as the material names, so an older layer still shows what
 * was picked. The server's dbo.AI_Plm_MaterialText is the same rule; this copy
 * exists because the box shows the DRAFT, which the server has not seen yet.
 *
 * Free text comes back untrimmed: it is the live value of a text box, and a
 * trailing space the analyst is in the middle of typing must survive.
 */
export function materialText(
  stored: string | null | undefined, materials: ReadonlyArray<PlmOption>,
): string {
  const text = stored ?? '';
  const parts = text.split(';').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.some(p => !OID.test(p))) return text;
  const names = parts
    .map(id => materials.find(m => m.id.toLowerCase() === id.toLowerCase())?.name ?? '')
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  // An id list none of whose materials exists any more reads as stored, not as nothing.
  return names.length ? names.join('; ') : text;
}

/**
 * Reads a group back as "A, B, C" — the ticked boxes, in the SAME order and
 * with the SAME labels as the Stereoscopic tab shows them; "—" when none is
 * ticked. A box with a free-text partner reads as the analyst's own wording
 * when they typed one ("Other" → "Mastic flecks"), and as its label otherwise,
 * so ticking "Other" with no text still shows up rather than vanishing.
 */
export function summarizeGroup(
  obs: PlmObservationInput,
  options: ReadonlyArray<StereoOption>,
  partner?: TextPartner,
): string {
  const on = options
    .filter(([key]) => obs[key])
    .map(([key, label]) => {
      if (partner && key === partner.gate) {
        const typed = (obs[partner.text] ?? '').trim();
        if (typed) return typed;
      }
      return label;
    });
  return on.length ? on.join(', ') : '—';
}
